import filter from 'lodash/filter';
import sortBy from 'lodash/sortBy';
import update from 'immutability-helper';
import forEach from 'lodash/forEach';
import get from 'lodash/get';

import {
  SCHEMA_EDITOR_INIT,
  SCHEMA_EDITOR_INIT_OPTIONS,
  SCHEMA_EDITOR_CLEAR_ERROR,
  SCHEMA_EDITOR_RESET,
  SCHEMA_EDITOR_UPDATE_INPUT_SCHEMA,
  SCHEMA_EDITOR_UPDATE_SAMPLES,
  SCHEMA_EDITOR_UPDATE_OUTPUT_SAMPLES,
  UPDATE_DATA_SAMPLES_SUCCESS,
  LOGOUT_COMPLETE,
} from '../../constants/actionTypes';

import { FilterTypes, DefaultsViewConstants } from '../../constants/schemaEditor';

import UIUtils from '../../lib/UIUtils';
import DataSamplesProcessor from '../../lib/DataSamplesProcessor';
import DataSchemaProcessor from '../../lib/DataSchemaProcessor';
import TransformsUtils from '../../lib/TransformsUtils';
import { map } from 'lodash';
import { mapKeys } from 'lodash';

const initialState = {
  dataSetId: null,

  //samples
  fromSamples: {
    fromUpdatedAt: null,
    fromFlat: null,
    fromNested: null,
    byAttrId: null,
    byMetaAttrId: null,
  },
  toSamples: {
    toUpdatedAt: null,
    toFlat: null,
    toNested: null,
    toRaw: null,
    byAttrId: null,
    byMetaAttrId: null,
  },

  //schema
  fromSchema: {
    flat: [],
    byAttrId: {},
    metaFlat: [],
    metaByAttrId: {},
  },
  toSchema: {
    flat: [],
    byAttrId: {},
    metaFlat: [],
    metaByAttrId: {},
    raw: {},
  },

  error: null,
  isEmptyOutput: false,
  metaDataSpec: null,

  filterDisplayOptions: [],
  defaultsDisplayOptions: [],

  lastInitialized: null,
};

export const schemaEditorReducer = (state = initialState, action) => {
  switch (action.type) {
    case SCHEMA_EDITOR_INIT:
      return initSchemaEditor(state, action);

    case SCHEMA_EDITOR_INIT_OPTIONS:
      return initSchemaEditorOptions(state, action);

    case SCHEMA_EDITOR_CLEAR_ERROR:
      return clearSchemaEditorError(state, action);

    case SCHEMA_EDITOR_UPDATE_INPUT_SCHEMA: {
      let { inputSchema } = action.payload;
      let inputFlat = processInputSchema(inputSchema);

      return {
        ...state,
        fromSchema: {
          flat: inputFlat,
          byAttrId: organizeSchemaByAttrId(inputFlat),
        },
      };
    }

    case SCHEMA_EDITOR_UPDATE_SAMPLES: {
      let { dataSetId, fromNested, fromFlat, toNested, toFlat, toRaw, fromUpdatedAt, toUpdatedAt } =
        action.payload;
      let toSamplesByAttrId = UIUtils.getSamplesByAttrId(toFlat);
      let fromSamplesByAttrId = UIUtils.getSamplesByAttrId(fromFlat);
      let toMetadataSamples = UIUtils.getMetadataSamplesByAttrId(toFlat);
      let fromMetadataSamples = UIUtils.getMetadataSamplesByAttrId(fromFlat);
      let metaSchema = getMetaSchemaFromSamples(fromNested);

      return {
        ...state,
        dataSetId,
        //samples
        fromSamples: {
          fromUpdatedAt,
          fromFlat,
          fromNested,
          byAttrId: fromSamplesByAttrId,
          byMetaAttrId: fromMetadataSamples,
        },
        toSamples: {
          toUpdatedAt,
          toFlat,
          toNested,
          toRaw,
          byAttrId: toSamplesByAttrId,
          byMetaAttrId: toMetadataSamples,
        },
        fromSchema: {
          ...state.fromSchema,
          ...metaSchema,
        },
      };
    }

    case UPDATE_DATA_SAMPLES_SUCCESS: {
      const { result, receivedAt } = action;
      let fromNested = [],
        fromFlat = [];

      forEach(result, (s) => {
        fromNested.push(DataSamplesProcessor.rorderSampleProperties(s));
        fromFlat.push(DataSamplesProcessor.flattenSamples(s));
      });

      let fromSamplesByAttrId = UIUtils.getSamplesByAttrId(fromFlat);
      let fromMetadataSamples = UIUtils.getMetadataSamplesByAttrId(fromFlat);
      let metaSchema = getMetaSchemaFromSamples(fromNested);

      return {
        ...state,
        fromSamples: {
          fromUpdatedAt: receivedAt,
          fromFlat,
          fromNested,
          byAttrId: fromSamplesByAttrId,
          byMetaAttrId: fromMetadataSamples,
        },
        fromSchema: {
          ...state.fromSchema,
          ...metaSchema,
        },
      };
    }

    case SCHEMA_EDITOR_UPDATE_OUTPUT_SAMPLES: {
      const {
        dataSetId,
        toNested,
        toFlat,
        toRaw,
        schemas,
        error,
        isEmptyOutput = false,
        toUpdatedAt,
      } = action.payload;

      let toSamplesByAttrId = UIUtils.getSamplesByAttrId(toFlat);
      let toMetadataSamples = UIUtils.getMetadataSamplesByAttrId(toFlat);
      let flattenedSchema = DataSchemaProcessor.flattenSchema(
        get(schemas, '[0].properties'),
        get(schemas, '[0].required'),
        0,
      );

      let { toSchema } = getTransformedSchemas(null, flattenedSchema, state);
      let metaSchema = getMetaSchemaFromSamples(toNested);

      let newState = {
        ...state,
        dataSetId,
        toSchema: {
          ...toSchema,
          ...metaSchema,
          raw: schemas?.[0] || {},
        },
        toSamples: {
          toUpdatedAt,
          toFlat,
          toNested,
          toRaw,
          byAttrId: toSamplesByAttrId,
          byMetaAttrId: toMetadataSamples,
        },
        error,
        isEmptyOutput,
      };

      return newState;
    }

    case LOGOUT_COMPLETE:
    case SCHEMA_EDITOR_RESET:
      return initialState;

    default:
      return state;
  }
};

export default schemaEditorReducer;

function initSchemaEditor(state, action) {
  let { dataSetId, inputSchema, outputSchema } = action.payload;

  //TODO: these never change so should be computed during state initialization!
  let filterDisplayOptions = initFilterDisplayOptions();
  let defaultsDisplayOptions = initDefaultsDisplayOptions();
  let transformedSchemas = getTransformedSchemas(inputSchema, outputSchema, state);

  return {
    ...initialState,
    ...transformedSchemas,
    dataSetId,
    filterDisplayOptions,
    defaultsDisplayOptions,
    lastInitialized: Date.now(),
  };
}

function getTransformedSchemas(inputSchema, outputSchema, state) {
  let { inputFlat, outputFlat } = initSchemasFromInput(inputSchema, outputSchema);

  return {
    fromSchema: {
      ...state.fromSchema,
      flat: inputFlat,
      byAttrId: organizeSchemaByAttrId(inputFlat),
    },
    toSchema: {
      ...state.toSchema,
      flat: outputFlat,
      byAttrId: organizeSchemaByAttrId(outputFlat),
    },
    metaDataSpec: TransformsUtils.initTx().getMetadataSpec(),
  };
}

function initSchemaEditorOptions(state, action) {
  const filterDisplayOptions = initFilterDisplayOptions();
  const defaultsDisplayOptions = initDefaultsDisplayOptions();

  return {
    ...state,
    filterDisplayOptions,
    defaultsDisplayOptions,
  };
}

function clearSchemaEditorError(state, action) {
  return {
    ...state,
    error: initialState.error,
    isEmptyOutput: initialState.isEmptyOutput,
  };
}

function initSchemasFromInput(inputSchema, outputSchema = null) {
  let inputSchemaData = processInputSchema(inputSchema);
  let outputSchemaData = [];

  forEach(outputSchema, (row, index) => {
    let newRow = update(row, {
      __idx: { $set: index },
      __rowType: { $set: 'schema-editor-row-default' },
      value: { $set: row.__txName },
      label: { $set: row.__name },
    });

    outputSchemaData.push(newRow);
  });

  return {
    inputFlat: inputSchemaData,
    outputFlat: outputSchemaData,
  };
}

function processInputSchema(inputSchema) {
  let inputSchemaData = [];

  forEach(inputSchema, (row, index) => {
    let newRow = update(row, {
      __idx: { $set: index },
      __rowType: { $set: 'schema-editor-row-default' },
      value: { $set: row.__txName },
      label: { $set: row.__name },
    });

    inputSchemaData.push(newRow);
  });
  return inputSchemaData;
}

function getMetaSchemaFromSamples(nestedSamples) {
  let metaFlat = DataSchemaProcessor.getSchemaFromSamples(
    map(nestedSamples, 'nexlaMetaData'),
    true,
  );
  let metaByAttrId = organizeSchemaByAttrId(metaFlat);

  return {
    metaFlat,
    metaByAttrId,
  };
}

function initFilterDisplayOptions() {
  let filterOptions = filter(FilterTypes, (t) => {
    return t.value !== 'FILTER_NONE';
  });

  let sortedOptions = sortBy(filterOptions, ['idx']);
  return sortedOptions;
}

function initDefaultsDisplayOptions() {
  let defaultsSortedOptions = sortBy(DefaultsViewConstants, ['idx']);
  return defaultsSortedOptions;
}

function organizeSchemaByAttrId(flatSchema) {
  return mapKeys(flatSchema, '__name');
}
